import React, {useCallback, useState} from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    FormControlLabel,
    IconButton,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import QuillEditor from './QuillEditor';
import ImageDropZone from './ImageDropZone';
import ImageWithFallback from './ImageWithFallback';
import Api from '../../api';

const QUILL_FORMATS = [
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'header', 'blockquote',
    'align', 'color',
    'script',
];

interface DetailsTabProps {
    readOnly?: boolean;
    companyId: string;
    logo: string | null;
    setLogo: (v: string | null) => void;
    shortDescription: string;
    setShortDescription: (v: string) => void;
    description: string;
    setDescription: (v: string) => void;
    published: boolean;
    setPublished: (v: boolean) => void;
    canPublish: boolean;
    galleryCount: number;
    hours: string;
    setHours: (v: string) => void;
}

const DetailsTab: React.FC<DetailsTabProps> = (props) => {
    const readOnly = props.readOnly ?? false;
    const publishDisabled = !props.canPublish;
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const handleUploadLogo = useCallback(async (files: File[]) => {
        if (files.length === 0) return;
        setUploading(true);
        setUploadError(null);
        try {
            const result = await Api.Image.upload(
                props.companyId, 'logo', files[0],
            );
            props.setLogo(result.url);
        } catch {
            setUploadError('Das Logo konnte nicht hochgeladen werden.');
        } finally {
            setUploading(false);
        }
    }, [props.companyId, props.setLogo]);

    const handleRemoveLogo = useCallback(() => {
        props.setLogo(null);
    }, [props.setLogo]);

    return (
        <Box>
            {/* Logo Section */}
            <Card sx={{ marginBottom: 2 }}>
                <CardContent>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 2,
                    }}
                    >
                        <Typography variant={'body1'} sx={{ fontWeight: 'bold' }}>
                            Logo
                        </Typography>
                        {props.logo && !readOnly && (
                            <IconButton
                                onClick={handleRemoveLogo}
                                color={'error'}
                                size={'small'}
                            >
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </Box>
                    {!readOnly && (
                        <Typography
                            variant={'caption'}
                            color={'text.secondary'}
                            sx={{display: 'block', mb: 2}}
                        >
                            Änderungen am Logo werden erst beim Speichern übernommen.
                        </Typography>
                    )}
                    {props.logo ? (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'stretch',
                            gap: 2,
                        }}
                        >
                            <Box sx={{
                                width: readOnly ? '100%' : '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'grey.300',
                                padding: 1,
                            }}
                            >
                                <ImageWithFallback
                                    src={props.logo}
                                    sx={{
                                        maxWidth: '100%',
                                        maxHeight: 120,
                                        objectFit: 'contain',
                                    }}
                                    fallbackSx={{
                                        width: '100%',
                                        height: 120,
                                    }}
                                />
                            </Box>
                            {!readOnly && (
                                <Box sx={{
                                    width: '50%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                }}
                                >
                                    <ImageDropZone
                                        onFilesSelected={handleUploadLogo}
                                        multiple={false}
                                        label={'Logo ersetzen'}
                                        disabled={uploading}
                                    />
                                </Box>
                            )}
                        </Box>
                    ) : readOnly ? (
                        <Typography variant={'body2'} color={'text.secondary'}>
                            Kein Logo vorhanden.
                        </Typography>
                    ) : (
                        <ImageDropZone
                            onFilesSelected={handleUploadLogo}
                            multiple={false}
                            label={'Logo hochladen'}
                            disabled={uploading}
                        />
                    )}
                    {uploadError && (
                        <Alert severity={'error'} sx={{ marginTop: 1 }} onClose={() => setUploadError(null)}>
                            {uploadError}
                        </Alert>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Typography variant={'body1'} sx={{ marginBottom: 2 }}>
                        Kurzbeschreibung
                    </Typography>
                    <TextField
                        label={'Kurzbeschreibung'}
                        variant={'outlined'}
                        fullWidth={true}
                        size={'small'}
                        multiline={true}
                        minRows={2}
                        maxRows={4}
                        value={props.shortDescription}
                        onChange={event => props.setShortDescription(event.target.value)}
                        helperText={`${props.shortDescription.length} / 300 Zeichen`}
                        inputProps={{ maxLength: 300 }}
                        disabled={readOnly}
                    />

                    <Typography variant={'body1'} sx={{ marginTop: 3, marginBottom: 1 }}>
                        Beschreibung
                    </Typography>
                    <Box sx={{
                        '& .ql-container': { minHeight: '200px' },
                        '& .ql-editor': { minHeight: '200px' },
                        marginBottom: 3,
                    }}
                    >
                        <QuillEditor
                            value={props.description}
                            onChange={props.setDescription}
                            formats={QUILL_FORMATS}
                            placeholder={'Beschreiben Sie Ihren Betrieb...'}
                            readOnly={readOnly}
                        />
                    </Box>

                    <Typography variant={'body1'} sx={{ marginTop: 3, marginBottom: 1 }}>
                        Öffnungszeiten
                    </Typography>
                    <TextField
                        label={'Öffnungszeiten'}
                        variant={'outlined'}
                        fullWidth={true}
                        size={'small'}
                        multiline={true}
                        minRows={2}
                        maxRows={4}
                        value={props.hours}
                        onChange={event => props.setHours(event.target.value)}
                        placeholder={'z.B. Mo–Fr 10–18 Uhr, Sa 10–14 Uhr'}
                        helperText={'Optional – wird auf der Partnerseite angezeigt'}
                        disabled={readOnly}
                    />

                    <Box sx={{ marginTop: 3 }}>
                        <Tooltip
                            title={publishDisabled
                                ? 'Mindestens 5 öffentliche Bilder sowie eine Kurz- und '
                                    + 'Langbeschreibung sind erforderlich'
                                : 'Betrieb auf der Partnerseite veröffentlichen'
                            }
                        >
                            <span>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={props.published}
                                            onChange={(_, checked) => props.setPublished(checked)}
                                            disabled={publishDisabled || readOnly}
                                            color={'primary'}
                                        />
                                    }
                                    label={'Veröffentlichen'}
                                />
                            </span>
                        </Tooltip>
                        {publishDisabled && (
                            <Alert severity={'info'} sx={{ marginTop: 1 }}>
                                Zum Veröffentlichen werden mindestens 5 öffentliche Bilder
                                in der Galerie sowie eine Kurz- und Langbeschreibung benötigt.
                                (Aktuell: {props.galleryCount} öffentliche Bilder,
                                Kurzbeschreibung: {props.shortDescription ? 'vorhanden' : 'fehlt'},
                                Beschreibung: {props.description
                                    && props.description !== '<p><br></p>'
                                    && props.description !== '<p></p>'
                                    && props.description.replace(/<[^>]*>/g, '').trim().length > 0
                                    ? 'vorhanden' : 'fehlt'})
                            </Alert>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default DetailsTab;
